<template>
  <div :class="wrapperClasses">
    <svg
      class="circle-progress__svg"
      viewBox="-2 -2 108 108"
      :width="getSize"
      :height="getSize"
    >
      <circle
        class="circle-progress--circle circle-progress-fill-line--back"
        :r="radius"
        cx="52"
        cy="52"
      />
      <circle
        ref="fillingCircle"
        :class="fillCircleClasses"
        :style="fillingCircleStyles"
        :r="radius"
        cx="52"
        cy="52"
        transform="rotate(270 52 52)"
      />
    </svg>

    <div class="circle-progress--content">
      <span
        v-if="percentage"
        class="circle-progress__percentage"
      >
        {{ getPercentage }}
      </span>
      <!--
        SLOT: default
        Default content slot of the indicator.
        Will show inside indicator circle and overlaps it if content is too big.
      -->
      <slot />
    </div>
  </div>
</template>

<script>
import {ref, defineComponent, reactive} from 'vue';

export default defineComponent({
  props: {
    /**
     * PROP: max
     * Maximum value used to calculate percentage to fill indicator.
     * Used with value to display current percent loaded.
     * If either are undefined, infinite loading animation will display.
     **/
    max: {
      type: Number,
      required: false,
      default: undefined
    },
    /**
     * PROP: value
     * Current value used to calculate percentage to fill indicator.
     * Used with max to display current percent loaded.
     * If either are undefined, infinite loading animation will display.
     **/
    value: {
      type: Number,
      required: false,
      default: undefined
    },
    /**
     * PROP: percentage
     * Turn on to display calculated percentage in the center of the indicator.
     **/
    percentage: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * PROP: size
     * Size of indicator.
     * Can be either a number that will be the width and height pixel measurements
     *  OR one of the following string options:
     *  - large      = 88px
     *  - small      = 56px
     *  - icon-large = 24px
     *  - icon-small = 16px
     * Options: 'large', 'small', 'icon-large', 'icon-small'
     **/
    size: {
      type: [String, Number],
      required: false,
      default: 'large',
      validator: (value) => [
        'large',
        'small',
        'icon-large',
        'icon-small'
      ].includes(value)
    },
    speed: {
      type: String,
      default: "slow",
      validator: (value) => [
        'slow',
        'fast'
      ].includes(value)
    },

    /**
     * A bit of a hack, if you want the percent indicator / spinner to stop at 99 %
     */
    preventFullPercent: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    props = reactive(props);
    return {
      fillingCircle: ref(null),
      radius: ref(48),
    };
  },
  computed: {
    wrapperClasses() {
      return {
        'circle-progress': true,
        'circle-progress--small': this.getSize < 32
      };
    },
    fillCircleClasses() {
      return {
        "circle-progress--circle": true,
        "circle-progress-fill--value": !this.noProgress,
        "circle-progress-fill--no-value": this.noProgress,
        "circle-progress-fill-line": true,
        "speed-fast": this.speed === "fast"
      };
    },
    noProgress() {
      return !this.max || !this.value;
    },
    getSize() {
      if (this.size === 'large') {
        return 88;
      } else if (this.size === 'small') {
        return 56;
      } else if (this.size === 'icon-large') {
        return 24;
      } else if (this.size === 'icon-small') {
        return 16;
      } else if (typeof this.size == 'number') {
        return this.size;
      } else {
        return 88;
      }
    },
    isLimitReached() {
      if (this.noProgress) return false;
      return this.max <= this.value;
    },
    getPercentage() {
      if (this.noProgress) return "";
      let percentage = Math.floor(this.value / this.max * 100);
      if(this.preventFullPercent && percentage >= 100) {
        percentage = 99;
      }
      return percentage.toString() + '%';
    },
    dashArray() {
      return (this.radius * Math.PI * 2);
    },
    dashOffset() {
      if (this.noProgress) return this.dashArray * 2;
      return this.dashArray - this.dashArray * this.currentFormatted / this.max;
    },
    currentFormatted() {
      return this.isLimitReached ? this.max : this.value;
    },
    fillingCircleStyles() {
      return {
        'strokeDashoffset': this.dashOffset,
        'strokeDasharray': this.dashArray,
      };
    }
  },
  methods: {},
});
</script>

<style lang="scss" scoped>
@keyframes filling {
  from {
    stroke-dashoffset: v-bind("dashArray");
  }
  to {
    stroke-dashoffset: v-bind("dashOffset");
  }
}

@keyframes spinning {
  0% {
    stroke-dashoffset: 0;
  }
  66% {
    stroke-dashoffset: v-bind("dashArray");;
  }
}

.circle-progress-fill--value {
  animation-name: filling;
  animation-duration: 2s;
  animation-timing-function: ease-in-out;
  transition: 0.5s all;
}

.circle-progress-fill--no-value {
  animation-name: spinning;
  animation-duration: 2s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite; /* Run the animation continuously */
  transition: 0.5s all;

  &.speed-fast {
    animation-duration: 1s;
    transition: 3s all;
  }
}

:deep(.circle-progress),
.circle-progress {
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;

  &.circle-progress--small .circle-progress--circle {
    stroke-width: 12px;
  }

  .circle-progress--circle {
    fill: transparent;
    stroke-width: 8px;
  }

  .circle-progress-fill-line--back {
    stroke: transparent;
    stroke-dashoffset: 0;
  }

  .circle-progress-fill-line {
    @apply stroke-default_primary_dark;
  }

  .circle-progress--content {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
}

</style>
